import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, X } from 'lucide-react'
import { useKillSwitch } from '@/hooks/useKillSwitch'
import { cn } from '@/utils'

export function KillSwitchConfirmDialog() {
  const { isConfirmOpen, isActivating, error, closeConfirm, activate } = useKillSwitch()

  const handleActivate = () => {
    activate('User activated emergency stop')
  }

  return (
    <AnimatePresence>
      {isConfirmOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={closeConfirm}
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-lg"
          >
            <div className="bg-light-card dark:bg-dark-card rounded-xl shadow-xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="bg-danger p-lg flex items-center gap-md">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-title-lg text-white">
                    Emergency Stop
                  </h2>
                  <p className="text-body-sm text-white/80">
                    This will halt all active tasks
                  </p>
                </div>
                <button
                  onClick={closeConfirm}
                  className="p-xs text-white/70 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-lg space-y-lg">
                <p className="text-body-md text-light-text-secondary dark:text-dark-text-secondary">
                  Are you sure you want to activate the kill switch? This action will:
                </p>

                <ul className="space-y-sm text-body-md text-light-text-primary dark:text-dark-text-primary">
                  <li className="flex items-start gap-sm">
                    <span className="text-danger mt-1">•</span>
                    Cancel all running tasks immediately
                  </li>
                  <li className="flex items-start gap-sm">
                    <span className="text-danger mt-1">•</span>
                    Stop all pending scheduled payments
                  </li>
                  <li className="flex items-start gap-sm">
                    <span className="text-danger mt-1">•</span>
                    Disconnect from trading systems
                  </li>
                </ul>

                {error && (
                  <div className="p-md bg-danger/10 border border-danger/20 rounded-md">
                    <p className="text-body-sm text-danger">{error}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-lg pt-0 flex gap-md">
                <button
                  onClick={closeConfirm}
                  className="btn-secondary flex-1"
                  disabled={isActivating}
                >
                  Cancel
                </button>
                <motion.button
                  onClick={handleActivate}
                  disabled={isActivating}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    'btn-danger flex-1 flex items-center justify-center gap-sm',
                    isActivating && 'opacity-70 cursor-not-allowed'
                  )}
                >
                  {isActivating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Stopping...</span>
                    </>
                  ) : (
                    'Activate Kill Switch'
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
